import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import { storage } from "@/utils/storage";
import { api, TOKEN_KEY } from "@/lib/api";
import { applyTheme, getTheme } from "@/lib/theme";

const AuthContext = createContext(undefined);

const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

function parseSessionId(url) {
  if (!url) return null;
  try {
    const hashIdx = url.indexOf("#");
    const qIdx = url.indexOf("?");
    let tail = "";
    if (hashIdx >= 0) tail = url.slice(hashIdx + 1);
    else if (qIdx >= 0) tail = url.slice(qIdx + 1);
    if (!tail) return null;
    const params = new URLSearchParams(tail);
    return params.get("session_id");
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [justAuthed, setJustAuthed] = useState(false);

  const processSessionId = useCallback(async (session_id) => {
    try {
      const res = await api.createSession(session_id);
      await storage.secureSet(TOKEN_KEY, res.session_token);
      setUser(res.user);
      setJustAuthed(true); // triggers the magical splash on Login → /mirror
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("session exchange failed", e);
    }
  }, []);

  const consumeJustAuthed = useCallback(() => setJustAuthed(false), []);

  // Theme saved in the profile wins on login (applied without animation)
  useEffect(() => {
    const t = user?.theme;
    if ((t === "day" || t === "night") && t !== getTheme()) applyTheme(t, { persist: false, animate: false });
  }, [user?.theme]);

  const refreshUser = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
    } catch {
      await storage.secureRemove(TOKEN_KEY);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Parse session_id from window URL (Emergent Auth returns it here)
        if (typeof window !== "undefined") {
          const sid =
            parseSessionId(window.location.hash) ||
            parseSessionId(window.location.search);
          if (sid) {
            await processSessionId(sid);
            window.history.replaceState(null, "", window.location.pathname);
            if (mounted) setLoading(false);
            return;
          }
        }
        // Existing token?
        const token = await storage.secureGet(TOKEN_KEY, "");
        if (token) await refreshUser();
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [processSessionId, refreshUser]);

  const signInWithGoogle = useCallback(async () => {
    const redirectUrl =
      typeof window !== "undefined"
        ? window.location.origin + "/"
        : "";
    const authUrl = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
    if (typeof window !== "undefined") {
      window.location.href = authUrl;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    await storage.secureRemove(TOKEN_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, signInWithGoogle, signOut, refreshUser, justAuthed, consumeJustAuthed }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
